import React, { useState, useMemo, useEffect, useRef } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import Icon from "../../../components/Icon";
import AgentAvatar from "./AgentAvatar";
import {
  AGENT_PALETTE, AGENT_PALETTE_KEYS,
  AGENT_ICON_LIBRARY, AGENT_ICON_BY_NAME,
  AGENT_ICON_CATEGORIES, AGENT_ICONS_BY_CATEGORY,
  resolveAgentVisual,
} from "./agentVisuals";

/**
 * AvatarPicker — single-screen modal for choosing an agent's color +
 * icon. Replaces the multi-tab pattern (Notion 2018-2022) with a
 * unified surface (Notion 2025+, Linear, Slack emoji picker).
 *
 * Sections (stacked, top to bottom):
 *   1. Live preview — shows what the agent will look like
 *   2. Color row — 12 swatches in a horizontal grid
 *   3. Icon search input + ~60-icon grid (filtered by name + keywords)
 *
 * Autosave: every click commits via onChange immediately. A small
 * "Undo last change" link surfaces inline for 5s after each change so
 * the user can revert a misclick without rummaging through the whole
 * grid again. Closing the picker (Esc or backdrop click) is also a
 * no-op — the user's changes are already persisted.
 *
 * Pattern reference (research):
 *   - Linear, Notion 2025: collapsed multi-tab pickers into single
 *     searchable surfaces.
 *   - GitLab/LogRocket on autosave: click → instant write → "Undo"
 *     toast (5s) is the canonical autosave UX for cosmetic settings.
 *
 * Props:
 *   agent         — full agent row (used for live preview seeding)
 *   value         — { color, iconName } current selection
 *   onChange(next) — called on every commit. Parent persists +
 *                    triggers re-render with new metadata_json.icon.
 *   onClose       — backdrop / Esc / Done button
 */

// Pure helper (named export for unit testing) — filters the icon library by a
// search string, matching both `name` and `keywords`. Extracted from the
// component's useMemo so it can be exercised directly; wired back below.
export function filterIcons(library, search) {
  if (!search || !search.trim()) return library;
  const q = search.trim().toLowerCase();
  return library.filter(e =>
    e.name.includes(q) || e.keywords.toLowerCase().includes(q),
  );
}

export default function AvatarPicker({ agent, value, onChange, onClose }) {
  const resolved = resolveAgentVisual({
    ...(agent || {}),
    metadata_json: { ...(agent?.metadata_json || {}), icon: value },
  });
  const [search, setSearch] = useState("");
  const [previousValue, setPreviousValue] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const dialogRef = useRef(null);

  // Esc dismiss + outside-click on backdrop.
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Filter icons. Search hits both name + keywords.
  const filteredIcons = useMemo(() => filterIcons(AGENT_ICON_LIBRARY, search), [search]);

  function commit(next) {
    setPreviousValue(value || null);
    onChange(next);
    if (undoTimer) clearTimeout(undoTimer);
    setUndoTimer(setTimeout(() => setPreviousValue(null), 5000));
  }

  function undoLast() {
    if (!previousValue && previousValue !== null) return;
    onChange(previousValue || { color: undefined, iconName: undefined });
    setPreviousValue(null);
    if (undoTimer) clearTimeout(undoTimer);
  }

  return (
    <div
      role="dialog"
      aria-label="Choose agent avatar"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        ref={dialogRef}
        style={{
          width: "min(640px, 94vw)",
          maxHeight: "88vh",
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          ...(MATERIALS?.thickGlass ? MATERIALS.thickGlass() : {}),
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
            Avatar
          </div>
          <button
            type="button" onClick={onClose}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              padding: 4, color: COLORS.textDim,
            }}
            title="Close"
          >
            <Icon name="action.close" size={14} color={COLORS.textDim} />
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Live preview */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <AgentAvatar
              agent={{ ...(agent || {}), metadata_json: { ...(agent?.metadata_json || {}), icon: value } }}
              size={64}
              variant="tile"
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                {agent?.name || "Agent"}
              </div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                {value?.color ? AGENT_PALETTE[value.color]?.name : "—"} · {" "}
                {value?.iconName ? AGENT_ICON_BY_NAME[value.iconName]?.name : "—"}
              </div>
            </div>
            {previousValue !== null && (
              <button
                type="button"
                onClick={undoLast}
                style={{
                  marginLeft: "auto",
                  padding: "5px 10px", fontSize: 11, fontWeight: 600,
                  color: COLORS.accentText, background: `${COLORS.accent}10`,
                  border: `1px solid ${COLORS.accent}30`,
                  borderRadius: 6, cursor: "pointer",
                }}
                title="Revert the last change (auto-clears in 5s)"
              >
                ↶ Undo
              </button>
            )}
          </div>

          {/* Color row — orb previews. 32 entries, wrap freely. */}
          <div>
            <div style={sectionLabel}>Color · {AGENT_PALETTE_KEYS.length}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {AGENT_PALETTE_KEYS.map(key => {
                const p = AGENT_PALETTE[key];
                const active = value?.color === key;
                const orb = `radial-gradient(circle at 30% 22%, ${p.highlight} 0%, ${p.base} 38%, ${p.shadow} 100%)`;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => commit({ ...(value || {}), color: key, iconName: value?.iconName || resolved.iconName })}
                    title={p.name}
                    style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: orb,
                      position: "relative",
                      border: active ? `2px solid ${COLORS.text}` : `2px solid transparent`,
                      boxShadow: `0 0 16px ${p.base}55, 0 4px 10px ${p.shadow}66, inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -1px 3px ${p.shadow}55`,
                      cursor: "pointer",
                      transition: "transform 0.12s, box-shadow 0.12s",
                      outline: "none",
                      padding: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.12) translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        `0 0 24px ${p.base}88, 0 6px 14px ${p.shadow}88, inset 0 1px 2px rgba(255,255,255,0.45), inset 0 -1px 3px ${p.shadow}55`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow =
                        `0 0 16px ${p.base}55, 0 4px 10px ${p.shadow}66, inset 0 1px 2px rgba(255,255,255,0.35), inset 0 -1px 3px ${p.shadow}55`;
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Icon search + grid */}
          <div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 8,
            }}>
              <div style={sectionLabel}>Icon</div>
              <div style={{ position: "relative", width: 200 }}>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search icons…"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "5px 10px 5px 26px", fontSize: 11,
                    color: COLORS.text, background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`, borderRadius: 6,
                    outline: "none",
                  }}
                />
                <span style={{
                  position: "absolute", left: 8, top: "50%",
                  transform: "translateY(-50%)", display: "flex",
                }}>
                  <Icon name="action.search" size={11} color={COLORS.textDim} />
                </span>
              </div>
            </div>
            <div style={{
              maxHeight: 360, overflowY: "auto", paddingRight: 4,
              display: "flex", flexDirection: "column", gap: 14,
            }}>
              {/* When the user is searching, render a flat grid (no
                  categories) ranked by relevance. Otherwise group by
                  category for navigability. */}
              {search.trim() ? (
                <IconGrid
                  entries={filteredIcons}
                  value={value}
                  resolvedColor={resolved.color}
                  onPick={(entry) => commit({ ...(value || {}), iconName: entry.name, color: value?.color || resolved.color })}
                />
              ) : (
                AGENT_ICON_CATEGORIES.map(cat => (
                  <div key={cat}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: COLORS.textDim,
                      textTransform: "uppercase", letterSpacing: "0.08em",
                      marginBottom: 6,
                    }}>{cat}</div>
                    <IconGrid
                      entries={AGENT_ICONS_BY_CATEGORY[cat]}
                      value={value}
                      resolvedColor={resolved.color}
                      onPick={(entry) => commit({ ...(value || {}), iconName: entry.name, color: value?.color || resolved.color })}
                    />
                  </div>
                ))
              )}
              {search.trim() && filteredIcons.length === 0 && (
                <div style={{
                  textAlign: "center", padding: "12px 0",
                  fontSize: 12, color: COLORS.textMuted,
                }}>
                  No icons match "{search}".
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 16px",
          borderTop: `1px solid ${COLORS.border}`,
          background: `${COLORS.border}10`,
          fontSize: 11, color: COLORS.textMuted,
        }}>
          <div>Changes save automatically</div>
          <button type="button" onClick={onClose} style={{
            padding: "6px 14px", fontSize: 11, fontWeight: 600,
            color: "#fff", background: COLORS.accent,
            border: "none", borderRadius: 6, cursor: "pointer",
          }}>Done</button>
        </div>
      </div>
    </div>
  );
}


// Reusable icon-grid renderer — used both in flat-search mode and
// in per-category sections. Active icon picks up the current swatch
// (so the picker reads as "preview of what your tile will look like"
// before commit).
function IconGrid({ entries, value, resolvedColor, onPick }) {
  const swatch = AGENT_PALETTE[value?.color || resolvedColor];
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
      gap: 6,
    }}>
      {entries.map(entry => {
        const active = value?.iconName === entry.name;
        const IconCmp = entry.Icon;
        return (
          <button
            key={entry.name}
            type="button"
            onClick={() => onPick(entry)}
            title={entry.name}
            style={{
              width: "100%", aspectRatio: "1 / 1",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: active ? `${swatch?.base || COLORS.accent}1F` : "transparent",
              border: `1px solid ${active ? (swatch?.base || COLORS.accent) : COLORS.border}`,
              borderRadius: 8,
              cursor: "pointer",
              transition: "all 0.12s",
              padding: 0,
            }}
            onMouseEnter={(e) => {
              if (!active) e.currentTarget.style.background = `${COLORS.surfaceHover || COLORS.border}40`;
            }}
            onMouseLeave={(e) => {
              if (!active) e.currentTarget.style.background = "transparent";
            }}
          >
            <IconCmp size={18} color={active ? swatch?.base || COLORS.accent : COLORS.textMuted} strokeWidth={2} />
          </button>
        );
      })}
    </div>
  );
}

const sectionLabel = {
  fontSize: 10, fontWeight: 700, color: COLORS.textDim,
  textTransform: "uppercase", letterSpacing: "0.06em",
  marginBottom: 8,
};
